import * as React from "react";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Calendar, CalendarPanel, CALENDAR_POPOVER_CLASS } from "../Calendar";
import type { CalendarProps } from "../Calendar";

export type TriggerAccessibilityProps =
  | { triggerLabel: string; triggerAriaLabel?: string; "aria-label"?: string }
  | { triggerAriaLabel: string; triggerLabel?: string; "aria-label"?: string }
  | { "aria-label": string; triggerLabel?: string; triggerAriaLabel?: string };

export interface DatePickerBaseProps {
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  error?: boolean;
  value?: Date | null | undefined;
  onChange?: (date: Date | undefined) => void;
  onSelect?: (date: Date | undefined) => void;
  placeholder: string;
  /** The popup's header, e.g. "Pick a day:". */
  title: string;
  /** The popup's close button. */
  closeLabel: string;
  /** The popup's Save -- the picked day applies only then. */
  saveLabel: string;
  cancelLabel: string;
  nextMonthLabel: string;
  previousMonthLabel: string;
  dir: "ltr" | "rtl";
  locale: Locale;
  dateFormat?: string;
  disabled?: boolean;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  calendarProps?: Partial<
    Omit<
      CalendarProps,
      | "mode"
      | "selected"
      | "onSelect"
      | "required"
      | "dir"
      | "locale"
      | "nextMonthLabel"
      | "previousMonthLabel"
    >
  >;
}

export type DatePickerProps = DatePickerBaseProps & TriggerAccessibilityProps;

export function DatePicker({
  id,
  ref,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  error,
  value,
  onChange,
  onSelect,
  placeholder,
  title,
  closeLabel,
  saveLabel,
  cancelLabel,
  nextMonthLabel,
  previousMonthLabel,
  dir,
  locale,
  dateFormat = "PP",
  disabled = false,
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  calendarProps,
  ...accessibilityProps
}: DatePickerProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  // The day picked in the open popup -- applied only on Save (Figma
  // 2818:116890); closing any other way drops it.
  const [draft, setDraft] = React.useState<Date | undefined>(
    value ?? undefined,
  );

  // Every opening starts from the current value, however it was opened.
  React.useEffect(() => {
    if (isOpen) setDraft(value ?? undefined);
  }, [isOpen, value]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    controlledOnOpenChange?.(nextOpen);
  };

  const handleSave = () => {
    onChange?.(draft);
    onSelect?.(draft);
    handleOpenChange(false);
  };

  const accessibleTriggerLabel =
    accessibilityProps.triggerLabel ??
    accessibilityProps.triggerAriaLabel ??
    accessibilityProps["aria-label"];

  const isInvalid = error || ariaInvalid === true || ariaInvalid === "true";
  const formattedDate = value ? format(value, dateFormat, { locale }) : null;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          id={id}
          aria-describedby={ariaDescribedby}
          aria-invalid={isInvalid ? true : undefined}
          type="button"
          variant="secondary"
          aria-label={accessibleTriggerLabel}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-start text-body-m-r gap-2",
            !value && "text-text-placeholder",
            isInvalid &&
              "border-fail-text text-fail-text focus-visible:border-fail-text",
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0 text-secondary-icon" />
          <span>{formattedDate ?? placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={CALENDAR_POPOVER_CLASS} align="start">
        <CalendarPanel
          title={title}
          closeLabel={closeLabel}
          saveLabel={saveLabel}
          cancelLabel={cancelLabel}
          onSave={handleSave}
          onCancel={() => handleOpenChange(false)}
          saveDisabled={draft === undefined}
        >
          <Calendar
            dir={dir}
            locale={locale}
            nextMonthLabel={nextMonthLabel}
            previousMonthLabel={previousMonthLabel}
            {...calendarProps}
            mode="single"
            selected={draft}
            onSelect={setDraft}
          />
        </CalendarPanel>
      </PopoverContent>
    </Popover>
  );
}
